import { useEffect, useRef, useState } from 'react'
import type { ImgHTMLAttributes } from 'react'

import { Loading } from '@/modules/shared/icons-special/Loading'
import { classNames } from '@/modules/shared/utils/classNames'

type LazyImageProps = {
  /** Image source URL */
  src: string
  /** Image alt text */
  alt: string
  /** Optional className for styling */
  className?: string
  /** Optional placeholder image while loading */
  placeholderSrc?: string
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'alt' | 'ref'>

/**
 * Lazy loads an image when it enters the viewport using Intersection Observer.
 * Inherits all native <img> props except 'src', 'alt', and 'ref'.
 * @param src - The image source URL.
 * @param alt - The image alt text.
 * @param className - Optional className for styling.
 * @param placeholderSrc - Optional placeholder image while loading.
 * @param rest - Other native image props.
 */
export function LazyImage({ src, alt, className, placeholderSrc, ...rest }: LazyImageProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true)
            observer.disconnect()
          }
        })
      },
      { threshold: 0.1 }
    )
    if (imgRef.current) {
      observer.observe(imgRef.current)
    }
    return () => observer.disconnect()
  }, [])

  return (
    <div className="relative inline-block size-full">
      {!loaded && isVisible && (
        <div className="absolute inset-0 flex size-full items-center justify-center bg-gray-100">
          <Loading className="size-6 text-gray-400" />
        </div>
      )}
      <img
        ref={imgRef}
        src={isVisible ? src : placeholderSrc}
        alt={alt}
        className={classNames(className, 'block transition-opacity duration-500', {
          'opacity-0': isVisible && !loaded,
          'opacity-100': loaded,
        })}
        onLoad={() => setLoaded(true)}
        loading="lazy"
        {...rest}
      />
    </div>
  )
}
